// Client-side export (W5, §20). The server only ever holds ciphertext, so the export
// file is assembled here from already-decrypted items. ExportItems is default-OFF and
// gated + audited server-side via api.exportOrg() before this runs. Files contain
// plaintext secrets — callers must warn the user.
import { templateFor } from "./catalog";
import type { DecryptedItem } from "./items";

function triggerDownload(filename: string, mime: string, data: string) {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has grabbed the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvCell(v: string): string {
  // Always quote; escape embedded quotes (RFC 4180). Prefix risky leading chars to
  // defuse spreadsheet formula injection.
  const safe = /^[=+\-@\t\r]/.test(v) ? "'" + v : v;
  return '"' + safe.replace(/"/g, '""') + '"';
}

// Flat CSV with the well-known columns plus one column per extra field. Secrets are
// included in plaintext (that is the point of an export) — the caller has been warned.
export function itemsToCSV(items: DecryptedItem[]): string {
  const base = ["name", "type", "folder", "tags", "notes"];
  // Collect the union of field labels across all items for stable columns.
  const extra: string[] = [];
  for (const it of items) {
    for (const f of it.content.fields) {
      if (!extra.includes(f.label)) extra.push(f.label);
    }
  }
  const header = [...base, ...extra];
  const rows = [header.map(csvCell).join(",")];
  for (const it of items) {
    const byLabel = new Map(it.content.fields.map((f) => [f.label, f.value]));
    const row = [
      it.content.name,
      templateFor(it.type).label,
      it.folderId ?? "",
      (it.content.tags ?? []).join(" "),
      it.content.notes ?? "",
      ...extra.map((l) => byLabel.get(l) ?? ""),
    ];
    rows.push(row.map(csvCell).join(","));
  }
  return rows.join("\r\n");
}

// Full-fidelity JSON: every field with its key/type/value preserved (round-trips back
// into the catalog model). This is the lossless format for re-import or migration.
export function itemsToJSON(items: DecryptedItem[]): string {
  return JSON.stringify(
    {
      format: "vault-export",
      version: 1,
      items: items.map((it) => ({
        name: it.content.name,
        type: it.type,
        tags: it.content.tags ?? [],
        notes: it.content.notes ?? "",
        fields: it.content.fields.map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          value: f.value,
          note: f.note ?? "",
        })),
      })),
    },
    null,
    2,
  );
}

export function downloadExport(orgName: string, items: DecryptedItem[], fmt: "csv" | "json", stamp: string) {
  const slug = orgName.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40) || "vault";
  if (fmt === "csv") {
    triggerDownload(`${slug}-${stamp}.csv`, "text/csv;charset=utf-8", itemsToCSV(items));
  } else {
    triggerDownload(`${slug}-${stamp}.json`, "application/json", itemsToJSON(items));
  }
}
